import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { Card } from 'components/atomic/atoms/card';
import Text from 'components/atomic/atoms/text';

export interface ContentSectionTemplateProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Section heading text (optional if hideHeading is true) */
  heading?: string;
  /** Section content */
  children: ReactNode;
  /** Whether to wrap content in a Card. Set to false when section has multiple sub-sections with their own cards. */
  withCard?: boolean;
  /** Whether to hide the heading. Use when the child component already renders its own heading. */
  hideHeading?: boolean;
  /** Scroll margin top for sticky header offset (px). Uses CSS custom property --scroll-offset if not provided. */
  scrollMarginTop?: number;
  /** Test ID for testing */
  testId?: string;
}

/**
 * ContentSectionTemplate - Standard layout for listing page content sections
 * 
 * Atomic Design: Template
 * - Semantic section element with ID for scroll targeting
 * - CSS scroll-margin-top for proper scroll-to positioning with sticky headers
 * - H2 heading with consistent typography
 * - Optional Card container with border variant
 */
export function ContentSectionTemplate({
  id,
  heading,
  children,
  withCard = true,
  hideHeading = false,
  scrollMarginTop,
  testId,
}: ContentSectionTemplateProps) {
  return (
    <Box
      component="section"
      id={id}
      data-testid={testId}
      sx={{
        // Use provided scrollMarginTop, or fall back to CSS custom property, or default
        scrollMarginTop: scrollMarginTop ? `${scrollMarginTop}px` : 'var(--scroll-offset, 150px)',
      }}
    >
      {!hideHeading && heading && (
        <Text component="h2" variant="h5" sx={{ mt: 3, mb: 2 }}>
          {heading}
        </Text>
      )}
      {withCard ? (
        <Card variant="border">
          {children}
        </Card>
      ) : (
        children
      )}
    </Box>
  );
}

/**
 * ContentSubSection - Sub-section within a ContentSectionTemplate
 * 
 * Use when a section has multiple logical parts that each need their own card.
 */
export interface ContentSubSectionProps {
  /** Sub-section heading text */
  heading: string;
  /** Sub-section content */
  children: ReactNode;
}

export function ContentSubSection({ heading, children }: ContentSubSectionProps) {
  return (
    <Card variant="border" sx={{ mb: 2 }}>
      <Text component="h3" variant="h6" sx={{ mb: 1.5 }}>
        {heading}
      </Text>
      {children}
    </Card>
  );
}

export default ContentSectionTemplate;
